/**
 * Help-centre search service.
 *
 * Calls Bower's own `/api/help/search`, which searches a LOCAL index of the
 * published help centre. The query never leaves Bower — not to Mintlify, not to
 * anyone — so there is no egress gate, no vendor dependency at request time,
 * and PHI workspaces get the same results as everyone else.
 */

import { apiService } from './api.service';

export interface HelpSearchResult {
  readonly title: string;
  readonly url: string;
  readonly snippet: string;
}

/** Why the index wasn't consulted. Absent on a healthy response. */
export type HelpSearchDegradedReason = 'index_unavailable';

export interface HelpSearchPayload {
  readonly results: readonly HelpSearchResult[];
  /** True when `results` is empty because something failed, not because nothing matched. */
  readonly degraded: boolean;
  readonly reason?: HelpSearchDegradedReason;
}

/**
 * The value callers get when the request itself fails. Same shape as a
 * server-side degrade, so the palette has ONE fallback branch to handle rather
 * than one per failure layer.
 */
const NETWORK_DEGRADED: HelpSearchPayload = {
  results: [],
  degraded: true,
  reason: 'index_unavailable',
};

class HelpService {
  /**
   * Search the help centre. Resolves to a degraded payload rather than
   * rejecting: a dead help search must cost the user live results, never the
   * whole palette.
   */
  async search(query: string): Promise<HelpSearchPayload> {
    try {
      // `request(..., 0)` rather than `get`: `get` defaults to ONE retry with a
      // 1s backoff, and the backend already answers 200-with-`degraded` instead
      // of 5xx on every failure it can see — so a retry only ever re-runs a
      // call that already gave up, doubling the wait for the same answer.
      // 12s sits just ABOVE the server's own corpus-fetch deadline (10s, in
      // help-index.service). Below it — as an earlier 8s did — the client
      // abandons a cold-start request the server was about to answer, including
      // one returning a perfectly good stale index. The wait costs the user
      // nothing: curated rows render throughout.
      const response = await apiService.request<HelpSearchPayload>(
        { method: 'GET', url: '/help/search', params: { q: query }, timeout: 12_000 },
        0,
      );
      return response.data ?? NETWORK_DEGRADED;
    } catch {
      return NETWORK_DEGRADED;
    }
  }
}

export const helpService = new HelpService();
